import {
    AreaPlot,
    ChartsAxisHighlight,
    ChartsGrid,
    ChartsTooltip,
    ChartsXAxis,
    ChartsYAxis,
    LinePlot,
    MarkPlot,
} from "@mui/x-charts";
import { ChartsLoadingOverlay } from "@mui/x-charts/ChartsOverlay";
import _ from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { CrremPathway } from "graphql-types/graphql";
import { useAssetFilterTranslations } from "hooks";
import {
    INITIAL_YEAR,
    ReductionAnalysisChartData,
    ReductionProposal,
} from "legacyNext/types";

import {
    ChartMark,
    ChartLegend,
    ChartTooltip,
    ChartItemTooltip,
} from "./components";
import { DiagonalHatch } from "./components/DiagonalHatch";
import { S } from "./reduction-analysis.styles";

type Props = {
    proposals: Array<ReductionProposal>;
    data: ReductionAnalysisChartData[];
    pathways: Omit<CrremPathway, "country" | "version" | "target">[];
    type: ChartDisplayType;
    loading: boolean;
};

export const ReductionAnalysisChart = (props: Props) => {
    const { t } = useTranslation();
    const { translations } = useAssetFilterTranslations();
    const { data, pathways, proposals, loading, type } = props;

    const [isMarkHovered, setIsMarkHovered] = useState(false);

    const pathwaySeries = _.chain(pathways)
        .groupBy("crremCategory")
        .map((pathways) => ({
            id: `crrem-${pathways[0].crremCategory.toLowerCase()}`,
            dataKey: `crrem-${pathways[0].crremCategory.toLowerCase()}`,
            label: `CRREM - ${translations[pathways[0].crremCategory]}`,
            color: pathways[0].color,
            curve: "natural" as const,
        }))
        .value();

    const series = [
        {
            id: "intensity",
            area: true,
            label:
                type === ChartDisplayType.EMISSIONS
                    ? t("common.labels.emissionIntensity", "emission intensity")
                    : t("common.labels.energyIntensity", "energy intensity"),
            dataKey: "int",
            description: t(
                "legacyNext.intensityChartDescription",
                "based on OIS data"
            ),
            color: "#9E9AE4",
        },
        {
            id: "projected",
            area: true,
            label: t("legacyNext.projectedIntensity", "projected intensity"),
            dataKey: "proj",
            color: "#B0ACFE",
        },
        {
            id: "reduction",
            label: t("legacyNext.reductionPlan", "reduction plan"),
            dataKey: "red",
            color: "#3579F6",
            showMark: (params: any) =>
                params?.position.getFullYear() === new Date().getFullYear() ||
                proposals
                    .map((e) => e.startYear)
                    .includes(params?.position.getFullYear()),
        },
        {
            id: "diff",
            dataKey: "diff",
            color: "#FF0000",
            hideTooltip: true,
            area: true,
        },
        ...pathwaySeries,
    ].map((s) => ({
        showMark: false,
        type: "line",
        valueFormatter: (value: number | null) => {
            if (value === null) return "-";
            if (type === ChartDisplayType.EMISSIONS) {
                return `${value.toFixed(2)} kg CO2e/m²`;
            }
            return `${value.toFixed(2)} kWh/m²`;
        },
        ...s,
    }));

    return (
        <>
            <S.ChartContainer
                height={600}
                dataset={data}
                yAxis={[{ tickNumber: 5 }]}
                xAxis={[
                    {
                        id: "Years",
                        dataKey: "date",
                        scaleType: "time",
                        min: new Date(INITIAL_YEAR, 0, 1),
                        max: new Date(2050, 0, 1),
                        tickNumber: 5,
                        valueFormatter: (date) => date.getFullYear().toString(),
                    },
                ]}
                series={series as any}
            >
                <DiagonalHatch />
                {loading && <ChartsLoadingOverlay />}
                <ChartsGrid horizontal vertical />
                <AreaPlot />
                <LinePlot />
                <ChartsXAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />
                <ChartsYAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />

                <MarkPlot
                    slots={{ mark: ChartMark as any }}
                    slotProps={{ mark: { proposals } as any }}
                    onMouseEnter={() => setIsMarkHovered(true)}
                    onMouseLeave={() => setIsMarkHovered(false)}
                />
                {!isMarkHovered && (
                    <ChartsTooltip slots={{ popper: ChartTooltip }} />
                )}
                {isMarkHovered && (
                    <ChartsTooltip
                        trigger="item"
                        slots={{ popper: ChartItemTooltip as any }}
                        slotProps={{ popper: { proposals } as any }}
                    />
                )}
                {!isMarkHovered && <ChartsAxisHighlight x="line" />}
            </S.ChartContainer>

            <ChartLegend series={series} />
        </>
    );
};
